import { useTranslation } from "react-i18next";

import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/lib/utils";
import { useGetPlanOfferingsQuery } from "@/redux/services/order-portal";

const OrderDetails = ({
  serviceType,
  order,
}: {
  serviceType: ServiceType;
  order: OrderState;
}) => {
  const { t } = useTranslation();

  const { data: planOfferings } = useGetPlanOfferingsQuery({
    order_type: order.hasExistingServiceProvider ? "Port-IN" : "New Order",
    customer_type: order.customerType as CustomerType,
    network:
      serviceType === "mobi-5g" || serviceType === "freedom-5g"
        ? "5G"
        : undefined,
    ic_number: order.nricNumber !== "" ? order.nricNumber : undefined,
    internal_portin: !!(
      order.existingServiceProvider?.name === "Infinity" ||
      order.existingServiceProvider?.name === "EASI"
    ),
    sales_channel: 7,
  });

  function getSelectedPlan(planId?: string) {
    if (planId || order.plan.id) {
      for (const planOffering of planOfferings ?? []) {
        const plan = planOffering.member.find(
          (member) =>
            member.bundle_id &&
            member.bundle_id.toString() === (planId ?? order.plan.id.toString())
        );

        if (plan) {
          return plan;
        }
      }
    }

    return;
  }

  function getRelations() {
    const selectedPlan = getSelectedPlan();

    return selectedPlan?.relation?.length ? selectedPlan.relation : undefined;
  }
  const relations = getRelations();

  function getVoicePrice() {
    const voicePrice = relations?.find(
      (relation) => relation.name === "Voice"
    )?.price_amount;
    return voicePrice !== undefined ? Number(voicePrice) : undefined;
  }
  const voicePrice = getVoicePrice();

  function getTotalPrice() {
    const selectedPlan = getSelectedPlan();

    const planPriceString = order.plan.price
      ? order.plan.price.substring(1)
      : "0.00";
    const planPrice = Number(planPriceString);
    const simPrice = order.sim?.price ?? 0;
    const devicePrice = order.device?.price ?? 0;
    const addonPrice = order.addon?.price ?? 0;
    const miscPrice =
      selectedPlan?.relation && selectedPlan.relation.length > 0
        ? selectedPlan.relation.reduce(
            (acc, relation) => acc + Number(relation.price_amount),
            0
          )
        : 0;

    return planPrice + simPrice + devicePrice + addonPrice + miscPrice;
  }

  return (
    <>
      <div className="space-y-6 text-sm">
        <div className="space-y-1">
          <p className="text-muted-foreground uppercase">
            {serviceType === "infinity"
              ? t("plan-summary.titles.internet")
              : t("plan-summary.titles.sim-card")}
          </p>
          <Separator />
          <div>
            <div className="flex items-center justify-between gap-4">
              <p>{order.plan.name} (x1)</p>
              <p className="text-base text-primary">{order.plan.price}</p>
            </div>
          </div>
        </div>
        {serviceType === "infinity" && voicePrice && (
          <div className="space-y-1">
            <p className="text-muted-foreground uppercase">
              {t("plan-summary.titles.voice")}
            </p>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <p>Voice</p>
              <p className="text-base text-primary">
                {formatCurrency(voicePrice)}
              </p>
            </div>
          </div>
        )}
        {serviceType !== "infinity" && order.sim && (
          <div className="space-y-1">
            <p className="text-muted-foreground uppercase">
              {t("plan-summary.titles.sim-option")}
            </p>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <p>{order.sim.name}</p>
              <p className="text-base text-primary">
                {formatCurrency(order.sim.price ?? 0)}
              </p>
            </div>
          </div>
        )}
        {order.device && (
          <div className="space-y-1">
            <p className="text-muted-foreground uppercase">
              {t("plan-summary.titles.device")}
            </p>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <p>{order.device.name}</p>
              <p className="text-base text-primary">
                {formatCurrency(order.device.price ?? 0)}
              </p>
            </div>
          </div>
        )}
        {order.addon && (
          <div className="space-y-1">
            <p className="text-muted-foreground uppercase">
              {t("plan-summary.titles.addon")}
            </p>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <p>{order.addon.name}</p>
              <p className="text-base text-primary">
                {formatCurrency(order.addon.price ?? 0)}
              </p>
            </div>
          </div>
        )}
        {relations && relations.length > 0 && (
          <div className="space-y-1">
            <p className="text-muted-foreground uppercase">
              {t("plan-summary.titles.other")}
            </p>
            <Separator />
            {relations.map((relation) => {
              if (relation.name === "Voice") {
                return null;
              }

              return (
                <div
                  className="flex items-center justify-between gap-4"
                  key={relation.id}
                >
                  <p>{relation.name}</p>
                  <p className="text-base text-primary">
                    {formatCurrency(Number(relation.price_amount))}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Separator className="bg-primary" />
      <div className="flex justify-between">
        <p>{t("labels.total")}</p>
        <p className="text-primary">{formatCurrency(getTotalPrice())}</p>
      </div>
    </>
  );
};

const EasiRechargeDetails = ({ state }: { state: EasiRechargeState }) => {
  const { t } = useTranslation();

  const price = Number(state.package.price);

  return (
    <>
      <div className="space-y-6 text-sm">
        <div className="space-y-1">
          <p className="text-muted-foreground uppercase">Package</p>
          <Separator />
          <div>
            <div className="flex items-center justify-between gap-4">
              <p>{`Easiload ${formatCurrency(price, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`}</p>
              <p className="text-base text-primary">{formatCurrency(price)}</p>
            </div>
          </div>
        </div>
      </div>
      <Separator className="bg-primary" />
      <div className="flex justify-between">
        <p>{t("labels.total")}</p>
        <p className="text-primary">{formatCurrency(price)}</p>
      </div>
    </>
  );
};

export { OrderDetails, EasiRechargeDetails };
